import React, { useState, useEffect, useRef } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAchievements, createAchievement, updateAchievement, deleteAchievement, uploadAchievementArtwork } from '../../services/achievementService';

const TARGET_TYPES = [
  { value: 'lines_read', label: 'Lines Read' },
  { value: 'quizzes_completed', label: 'Quizzes Completed' },
  { value: 'streak_days', label: 'Streak Days' },
  { value: 'xp_earned', label: 'XP Earned' },
];

const emptyForm = {
  achievement_name: '',
  description: '',
  target_type: 'lines_read',
  target_value: 1,
  xp_reward: 0,
  artwork_url: null,
};

const AchievementFormModal = ({ achievement, onClose, onSave }) => {
  const [form, setForm] = useState(achievement ? {
    achievement_name: achievement?.name || '',
    description: achievement?.description || '',
    target_type: achievement?.target_type || 'lines_read',
    target_value: achievement?.target_value ?? achievement?.requirement_value ?? 1,
    xp_reward: achievement?.xp_reward || 0,
    artwork_url: achievement?.artwork_url || null,
  } : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(achievement?.artwork_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleArtworkChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.type !== 'image/png' && file?.type !== 'image/jpeg' && file?.type !== 'image/webp') {
      setError('Please select a PNG, JPEG, or WebP image.');
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }
    setArtworkFile(file);
    setArtworkPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleRemoveArtwork = () => {
    setArtworkFile(null);
    setArtworkPreview(null);
    setForm(prev => ({ ...prev, artwork_url: null }));
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.achievement_name?.trim()) { setError('Achievement name is required.'); return; }
    if (!form?.description?.trim()) { setError('Description is required.'); return; }
    if (!form?.target_type) { setError('Target type is required.'); return; }
    if (!form?.target_value || form?.target_value < 1) { setError('Target value must be at least 1.'); return; }
    setSaving(true);
    setError(null);
    try {
      let artworkUrl = form?.artwork_url;
      if (artworkFile) {
        setUploading(true);
        artworkUrl = await uploadAchievementArtwork(artworkFile, achievement?.id || null);
        setUploading(false);
      }
      await onSave({ ...form, artwork_url: artworkUrl });
      onClose();
    } catch (err) {
      setUploading(false);
      setError(err?.message || 'Failed to save achievement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {achievement ? 'Edit Achievement' : 'Create Achievement'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Achievement Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form?.achievement_name}
              onChange={e => handleChange('achievement_name', e?.target?.value)}
              placeholder="e.g. Avid Reader"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description <span className="text-red-400">*</span></label>
            <textarea
              value={form?.description}
              onChange={e => handleChange('description', e?.target?.value)}
              placeholder="Describe what the student must do to earn this achievement..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Target Type <span className="text-red-400">*</span></label>
              <select
                value={form?.target_type}
                onChange={e => handleChange('target_type', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {TARGET_TYPES?.map(t => (
                  <option key={t?.value} value={t?.value}>{t?.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Target Value <span className="text-red-400">*</span></label>
              <input
                type="number"
                min="1"
                value={form?.target_value}
                onChange={e => handleChange('target_value', parseInt(e?.target?.value) || 1)}
                placeholder="e.g. 50"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">XP Reward</label>
            <input
              type="number"
              min="0"
              value={form?.xp_reward}
              onChange={e => handleChange('xp_reward', parseInt(e?.target?.value) || 0)}
              placeholder="0"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs text-muted-foreground mt-1">XP awarded to the student when they earn this achievement</p>
          </div>

          {/* Artwork Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Achievement Artwork (PNG)</label>
            {artworkPreview ? (
              <div className="flex items-center gap-3">
                <img
                  src={artworkPreview}
                  alt="Achievement artwork preview"
                  className="w-16 h-16 rounded-lg object-cover border border-border"
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef?.current?.click()}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Icon name="RefreshCw" size={12} />
                    Replace image
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveArtwork}
                    className="text-xs text-red-400 hover:underline flex items-center gap-1"
                  >
                    <Icon name="Trash2" size={12} />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef?.current?.click()}
                className="w-full flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
              >
                <Icon name="ImagePlus" size={22} />
                <span className="text-sm">Click to upload PNG artwork</span>
                <span className="text-xs">PNG, JPEG, WebP · Max 5MB</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleArtworkChange}
              className="hidden"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || uploading}>
              {uploading ? (
                <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Uploading…</span>
              ) : saving ? (
                <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Saving…</span>
              ) : (achievement ? 'Save Changes' : 'Create Achievement')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ achievement, onClose, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-sm shadow-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
            <Icon name="Trash2" size={18} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Delete Achievement</h3>
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-sm text-foreground">
          Are you sure you want to delete <span className="font-medium">"{achievement?.name}"</span>?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={deleting}>
            {deleting ? <span className="flex items-center gap-2"><Icon name="Loader2" size={14} className="animate-spin" />Deleting…</span> : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const getTargetTypeLabel = (type) => {
  return TARGET_TYPES?.find(t => t?.value === type)?.label || type || '—';
};

const AchievementManagement = () => {
  const { userProfile } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [createModal, setCreateModal] = useState(false);
  const [editAchievement, setEditAchievement] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const isSuperAdmin = userProfile?.role === 'superadmin';

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAchievements();
      setAchievements(data);
    } catch (err) {
      setError(err?.message || 'Failed to load achievements.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    const newItem = await createAchievement(form);
    setAchievements(prev => [newItem, ...prev]);
  };

  const handleEdit = async (form) => {
    const updated = await updateAchievement(editAchievement?.id, form);
    setAchievements(prev => prev?.map(a => a?.id === updated?.id ? updated : a));
  };

  const handleDelete = async () => {
    await deleteAchievement(deleteItem?.id);
    setAchievements(prev => prev?.filter(a => a?.id !== deleteItem?.id));
    setDeleteItem(null);
  };

  const filtered = achievements?.filter(a => {
    const matchSearch = !search ||
      a?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      a?.description?.toLowerCase()?.includes(search?.toLowerCase());
    const matchType = typeFilter === 'all' || a?.target_type === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbTrail />

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="Trophy" size={24} className="text-primary" />
                Achievement Management
              </h1>
              <p className="text-muted-foreground mt-1">Define achievements students can unlock through their progress</p>
            </div>
            {isSuperAdmin && (
              <Button onClick={() => setCreateModal(true)} className="flex items-center gap-2">
                <Icon name="Plus" size={16} />
                Create Achievement
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search achievements..."
                className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Target Types</option>
              {TARGET_TYPES?.map(t => (
                <option key={t?.value} value={t?.value}>{t?.label}</option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Achievements', value: achievements?.length, icon: 'Trophy', color: 'text-primary' },
              { label: 'Lines Read', value: achievements?.filter(a => a?.target_type === 'lines_read')?.length, icon: 'BookOpen', color: 'text-blue-400' },
              { label: 'Quiz Based', value: achievements?.filter(a => a?.target_type === 'quizzes_completed')?.length, icon: 'CheckSquare', color: 'text-green-400' },
              { label: 'Streak Based', value: achievements?.filter(a => a?.target_type === 'streak_days')?.length, icon: 'Flame', color: 'text-orange-400' },
            ]?.map(stat => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
                <div className={`${stat?.color}`}><Icon name={stat?.icon} size={20} /></div>
                <div>
                  <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
              <p className="text-red-400 font-medium">{error}</p>
              <Button variant="outline" onClick={loadAchievements} className="mt-3">Retry</Button>
            </div>
          ) : filtered?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Icon name="Trophy" size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
              <p className="text-foreground font-medium mb-1">No achievements found</p>
              <p className="text-muted-foreground text-sm">
                {search || typeFilter !== 'all' ? 'Try adjusting your filters.' : 'Create your first achievement to get started.'}
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Achievement</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Artwork</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Target Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Target Value</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">XP Reward</th>
                      {isSuperAdmin && (
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered?.map(achievement => (
                      <tr key={achievement?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Trophy" size={18} className="text-primary" />
                            </div>
                            <span className="font-medium text-foreground text-sm">{achievement?.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {achievement?.artwork_url ? (
                            <img
                              src={achievement?.artwork_url}
                              alt={`${achievement?.name} artwork`}
                              className="w-10 h-10 rounded-lg object-cover border border-border"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-muted/40 border border-border flex items-center justify-center">
                              <Icon name="ImageOff" size={14} className="text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-sm text-muted-foreground line-clamp-2 max-w-xs">{achievement?.description || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                            {getTargetTypeLabel(achievement?.target_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-foreground">
                            {(achievement?.target_value ?? achievement?.requirement_value)?.toLocaleString() || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-sm text-foreground">
                            <Icon name="Zap" size={13} className="text-yellow-400" />
                            {achievement?.xp_reward?.toLocaleString() || 0}
                          </span>
                        </td>
                        {isSuperAdmin && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setEditAchievement(achievement)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                                title="Edit achievement"
                              >
                                <Icon name="Pencil" size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteItem(achievement)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                title="Delete achievement"
                              >
                                <Icon name="Trash2" size={15} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
                Showing {filtered?.length} of {achievements?.length} achievement{achievements?.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      </div>
      {createModal && (
        <AchievementFormModal achievement={null} onClose={() => setCreateModal(false)} onSave={handleCreate} />
      )}
      {editAchievement && (
        <AchievementFormModal achievement={editAchievement} onClose={() => setEditAchievement(null)} onSave={handleEdit} />
      )}
      {deleteItem && (
        <DeleteConfirmModal achievement={deleteItem} onClose={() => setDeleteItem(null)} onConfirm={handleDelete} />
      )}
    </div>
  );
};

export default AchievementManagement;
